/**
 * useBookingDetail — 예약 상세 공통 Hook
 *
 * BookingDetailSheet + BookingDetailPageV2의 queries/mutations 합집합.
 * 어디서 열든 동일한 데이터·액션을 제공한다.
 */

import { useState, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { appointmentApi } from "@/api/appointment";
import { customerApi } from "@/api/customer";
import { clinicSettingsApi } from "@/api/settings";
import { medicalStaffApi } from "@/api/medical-staff";
import { handleApiError } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";
import type { AppointmentTimeline } from "@/types/appointment";
import { useToast } from "@/components/ui/use-toast";

export interface UseBookingDetailParams {
  appointment: AppointmentTimeline | null;
  enabled: boolean;
  /** 쿼리 무효화 후 추가 콜백 (예: Sheet onDataChange) */
  onDataChange?: () => void;
}

export function useBookingDetail({
  appointment,
  enabled,
  onDataChange,
}: UseBookingDetailParams) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ===== Local UI state =====
  const [rejectReason, setRejectReason] = useState("");
  const [isRejecting, setIsRejecting] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string>("");

  // Sync staffId with appointment prop
  useEffect(() => {
    setSelectedStaffId(appointment?.medicalStaffId?.toString() ?? "");
  }, [appointment?.medicalStaffId, appointment?.id]);

  // Reset form state when appointment changes
  useEffect(() => {
    setIsRejecting(false);
    setRejectReason("");
    setIsCancelling(false);
    setCancelReason("");
  }, [appointment?.id]);

  // ===== Queries =====

  const { data: statusHistory = [], isLoading: isHistoryLoading } = useQuery({
    queryKey: ["appointment-history", appointment?.id],
    queryFn: () => appointmentApi.getHistory(appointment!.id),
    enabled: !!appointment?.id && enabled,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const { data: bookingStats } = useQuery({
    queryKey: queryKeys.appointments.bookingStats(
      appointment?.customerId ?? undefined,
    ),
    queryFn: () =>
      customerApi.getCustomerBookingStats(appointment!.customerId!),
    enabled: !!appointment?.customerId && enabled,
    staleTime: 1000 * 60 * 5,
  });

  const { data: appointmentHistory, isLoading: isAppointmentHistoryLoading } =
    useQuery({
      queryKey: queryKeys.appointments.customer(
        appointment?.customerId ?? undefined,
      ),
      queryFn: () =>
        customerApi.getCustomerAppointments(appointment!.customerId!, 0, 10),
      enabled: !!appointment?.customerId && enabled,
      staleTime: 1000 * 60 * 5,
    });

  const { data: customerConsults } = useQuery({
    queryKey: queryKeys.consults.customer(appointment?.customerId ?? undefined),
    queryFn: () =>
      customerApi.getCustomerConsults(appointment!.customerId!, 0, 10),
    enabled: !!appointment?.customerId && enabled,
    staleTime: 1000 * 60 * 5,
  });

  const { data: clinicSettings } = useQuery({
    queryKey: ["clinic-settings"],
    queryFn: () => clinicSettingsApi.get(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const { data: staffList = [] } = useQuery({
    queryKey: ["medical-staff"],
    queryFn: () => medicalStaffApi.getAll(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  // ===== Computed =====

  const stats = {
    visitCount: bookingStats?.visitCount ?? 0,
    noShowCount: bookingStats?.noShowCount ?? 0,
    cancelCount: bookingStats?.cancelCount ?? 0,
  };

  const matchedProduct = useMemo(() => {
    if (!appointment?.department || !clinicSettings?.priceInfo) return null;
    const dept = appointment.department.trim().toLowerCase();
    const products = clinicSettings.priceInfo.filter(
      (p) => p.isVisible !== false,
    );
    return (
      products.find((p) => p.name.trim().toLowerCase() === dept) ||
      products.find((p) => dept.includes(p.name.trim().toLowerCase())) ||
      products.find((p) => p.name.trim().toLowerCase().includes(dept)) ||
      null
    );
  }, [appointment?.department, clinicSettings?.priceInfo]);

  const otherPendingAppointments = useMemo(() => {
    if (!appointmentHistory?.content || !appointment?.id) return [];
    const pendingStatuses = ["REQUESTED", "CHECKING", "CONFIRMED"];
    return appointmentHistory.content.filter(
      (appt) =>
        appt.id !== appointment.id && pendingStatuses.includes(appt.status),
    );
  }, [appointmentHistory, appointment?.id]);

  // ===== Query invalidation (PageV2 패턴 채택) =====

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["appointments"] });
    queryClient.invalidateQueries({ queryKey: ["appointments-timeline"] });
    queryClient.invalidateQueries({ queryKey: ["appointment-summary"] });
    queryClient.invalidateQueries({ queryKey: ["appointment-detail"] });
    queryClient.invalidateQueries({ queryKey: ["consults-list"] });
    queryClient.invalidateQueries({ queryKey: ["consults"] });
    onDataChange?.();
  };

  // ===== Mutations =====

  const assignStaffMutation = useMutation({
    mutationFn: ({ id, staffId }: { id: number; staffId: number | null }) =>
      appointmentApi.assignStaff(id, staffId),
    onSuccess: (_data, variables) => {
      setSelectedStaffId(variables.staffId?.toString() ?? "");
      invalidateAll();
      toast({
        title: "의료진 배정 완료",
        description: "담당 의료진이 변경되었습니다.",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "의료진 배정 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: ({
      id,
      selectedPriority,
    }: {
      id: number;
      selectedPriority?: number;
    }) => appointmentApi.approve(id, selectedPriority),
    onSuccess: () => {
      invalidateAll();
      toast({ title: "예약 승인 완료", description: "예약이 확정되었습니다." });
    },
    onError: (error: unknown) => {
      toast({
        title: "예약 승인 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason?: string) =>
      appointmentApi.reject(appointment!.id, reason),
    onSuccess: () => {
      invalidateAll();
      toast({ title: "예약 거절 완료", description: "예약이 거절되었습니다." });
      setIsRejecting(false);
      setRejectReason("");
    },
    onError: (error: unknown) => {
      toast({
        title: "예약 거절 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  const noShowMutation = useMutation({
    mutationFn: () => appointmentApi.markNoShow(appointment!.id),
    onSuccess: () => {
      invalidateAll();
      toast({
        title: "노쇼 처리 완료",
        description: "예약이 노쇼로 처리되었습니다.",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "노쇼 처리 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => appointmentApi.markComplete(appointment!.id),
    onSuccess: () => {
      invalidateAll();
      toast({
        title: "이용완료 처리",
        description: "예약이 완료 처리되었습니다.",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "완료 처리 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (reason?: string) =>
      appointmentApi.cancel(appointment!.id, reason),
    onSuccess: () => {
      invalidateAll();
      toast({ title: "예약 취소 완료", description: "예약이 취소되었습니다." });
      setIsCancelling(false);
      setCancelReason("");
    },
    onError: (error: unknown) => {
      toast({
        title: "예약 취소 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  const toggleBlockMutation = useMutation({
    mutationFn: ({ blocked, reason }: { blocked: boolean; reason?: string }) =>
      customerApi.toggleBlock(appointment!.customerId!, blocked, reason),
    onSuccess: (_data, variables) => {
      invalidateAll();
      toast({
        title: variables.blocked ? "예약 차단" : "예약 차단 해제",
        description: variables.blocked
          ? "해당 고객의 AI 자동 예약이 차단됩니다."
          : "해당 고객의 예약 차단이 해제되었습니다.",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "차단 상태 변경 실패",
        description: handleApiError(error),
        variant: "destructive",
      });
    },
  });

  // ===== Handlers =====

  const handleApprove = (selectedPriority?: number) => {
    if (!appointment) return;
    const hasBothChoices =
      appointment.firstChoiceDate && appointment.secondChoiceDate;

    if (hasBothChoices && !selectedPriority) {
      toast({
        title: "우선순위 선택 필요",
        description:
          "1순위와 2순위 예약이 모두 있습니다. 원하는 일시를 선택해주세요.",
        variant: "destructive",
      });
      return;
    }

    const priority =
      selectedPriority ?? appointment.isPriorityChoice ?? undefined;

    if (confirm("이 예약을 승인하시겠습니까?")) {
      approveMutation.mutate({
        id: appointment.id,
        selectedPriority: priority,
      });
    }
  };

  const handleReject = () => {
    rejectMutation.mutate(rejectReason.trim() || undefined);
  };

  const handleNoShow = () => {
    if (confirm("이 예약을 노쇼 처리하시겠습니까?")) {
      noShowMutation.mutate();
    }
  };

  const handleComplete = () => {
    if (confirm("이 예약을 이용완료 처리하시겠습니까?")) {
      completeMutation.mutate();
    }
  };

  const handleCancel = () => {
    cancelMutation.mutate(cancelReason || undefined);
  };

  return {
    // Queries
    statusHistory,
    isHistoryLoading,
    bookingStats,
    stats,
    appointmentHistory,
    isAppointmentHistoryLoading,
    customerConsults,
    clinicSettings,
    staffList,
    matchedProduct,
    otherPendingAppointments,
    // Mutations
    approveMutation,
    rejectMutation,
    cancelMutation,
    noShowMutation,
    completeMutation,
    assignStaffMutation,
    toggleBlockMutation,
    // UI state
    rejectReason,
    setRejectReason,
    isRejecting,
    setIsRejecting,
    cancelReason,
    setCancelReason,
    isCancelling,
    setIsCancelling,
    isRescheduleOpen,
    setIsRescheduleOpen,
    selectedStaffId,
    setSelectedStaffId,
    // Handlers
    handleApprove,
    handleReject,
    handleNoShow,
    handleComplete,
    handleCancel,
    invalidateAll,
  };
}
